import { useEffect, useState } from 'react';
import { credentialsApi } from '../lib/api';

/** Diplômes : types, signataires, revue, émission, révocation, vérification publique. */
export function CredentialsAdminPage() {
  const [types, setTypes] = useState<any[]>([]);
  const [signers, setSigners] = useState<any[]>([]);
  const [serial, setSerial] = useState('');
  const [verified, setVerified] = useState<any>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => { reload(); }, []);

  async function reload() {
    try { setTypes(await credentialsApi.types()); } catch { /* réservé */ }
    try { setSigners(await credentialsApi.signers()); } catch { /* réservé */ }
  }

  async function run(fn: () => Promise<any>, note: string) {
    setError(''); setNotice('');
    try { await fn(); setNotice(note); await reload(); }
    catch (e: any) { setError(String(e.message || e)); }
  }

  const ask = (l: string, d = '') => window.prompt(l, d) ?? '';

  return <section className="page">
    <header className="page-header">
      <div>
        <span className="eyebrow">DIPLÔMES</span>
        <h1>Attestations et signataires</h1>
        <p>Un signataire actif est nécessaire pour que la plateforme puisse délivrer une attestation.</p>
      </div>
    </header>

    {error && <div className="alert error">{error}</div>}
    {notice && <div className="notice">{notice}</div>}

    <div className="grid two">
      <div className="card">
        <h3>Types d'attestation ({types.length})</h3>
        <button className="button secondary" onClick={() => {
          const code = ask('Code'); if (!code) return;
          const title = ask('Intitulé') || code;
          const kind = ask('Nature (DIPLOMA, ATTESTATION, CERTIFICATE)', 'ATTESTATION');
          run(() => credentialsApi.createType({ code, title, kind, requires_degree_audit: false }), 'Type créé.');
        }}>Ajouter un type</button>
        {types.map(t => <div key={t.id} className="order-row">
          <strong>{t.code} — {t.title}</strong><span>{t.kind}</span>
        </div>)}
        {!types.length && <p className="technical-note">Aucun type.</p>}
      </div>

      <div className="card">
        <h3>Signataires ({signers.length})</h3>
        <p className="technical-note">Sans signataire actif, le parcours autonome se termine sans attestation.</p>
        <button className="button secondary" onClick={() => {
          const userId = ask('Identifiant utilisateur du signataire'); if (!userId) return;
          const title = ask('Titre', 'Autorité de certification');
          run(() => credentialsApi.addSigner({ user_id: userId, title }), 'Signataire enregistré.');
        }}>Ajouter un signataire</button>
        {signers.map(s => <div key={s.id} className="order-row">
          <strong>{s.title}</strong><span>{(s.user_id || '').slice(0, 8)} · {s.active ? 'actif' : 'inactif'}</span>
        </div>)}
        {!signers.length && <p className="technical-note">Aucun signataire.</p>}
      </div>
    </div>

    <div className="card">
      <h3>Traiter une demande</h3>
      <div className="toolbar">
        <button className="button secondary" onClick={() => {
          const rid = ask('Identifiant de la demande'); if (!rid) return;
          run(() => credentialsApi.review(rid, 'APPROVED', ''), 'Demande approuvée.');
        }}>Approuver</button>
        <button className="button secondary" onClick={() => {
          const rid = ask('Identifiant de la demande'); if (!rid) return;
          const signerId = signers[0]?.id || ask('Identifiant du signataire');
          if (signerId) run(() => credentialsApi.issue(rid, signerId), 'Attestation émise.');
        }}>Émettre</button>
        <button className="button secondary" onClick={() => {
          const iid = ask('Identifiant de l\'attestation émise'); if (!iid) return;
          const reason = ask('Motif de révocation') || 'non précisé';
          run(() => credentialsApi.revoke(iid, reason), 'Attestation révoquée.');
        }}>Révoquer</button>
      </div>
    </div>

    <div className="card">
      <h3>Vérification publique</h3>
      <p className="technical-note">Vérifiable par un tiers à partir du seul numéro de série.</p>
      <label>Numéro de série<input value={serial} onChange={(e: any) => setSerial(e.target.value)} placeholder="EDU-..." /></label>
      <button className="button primary" disabled={!serial.trim()} onClick={async () => {
        setError(''); setVerified(null);
        try { setVerified(await credentialsApi.verify(serial.trim())); }
        catch (e: any) { setError(String(e.message || e)); }
      }}>Vérifier</button>
      {verified && <pre className="json-block">{JSON.stringify(verified, null, 2)}</pre>}
    </div>
  </section>;
}
